
import React, { useState, useEffect } from 'react';
import type { Athlete } from '../types';
import { type DraftingStats as DraftingStatsData, enrichAthletesWithUserProfile } from '../services/draftingAnalysis';
import { cloudAnalysisService } from '../services/cloudAnalysisService';
import { useAuth } from '../contexts/AuthContext';

interface DraftingStatsProps {
    athletes: Athlete[];
    raceId: string;
}

const formatTime = (seconds: number) => {
    if (isNaN(seconds) || seconds < 0) return '0m 0s';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
}

const DraftingStats: React.FC<DraftingStatsProps> = ({ athletes, raceId }) => {
    const { currentUser, userProfile } = useAuth();
    const [stats, setStats] = useState<DraftingStatsData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    
    useEffect(() => {
        let isMounted = true;
        
        const processStats = async () => {
            if (!athletes || athletes.length === 0 || !raceId) {
                setStats([]);
                return;
            }
            setIsLoading(true);
            
            try {
                // Enrich athletes with user's height, weight, and bike weight
                const enrichedAthletes = enrichAthletesWithUserProfile(athletes, currentUser?.uid, userProfile);
                
                // Fetch from Cloud Function
                const results = await cloudAnalysisService.getDraftingStats(enrichedAthletes, raceId);
                if (isMounted) {
                    setStats(results);
                }
            } catch (e) {
                console.error("Drafting stats load failed", e);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        processStats();
        
        return () => {
            isMounted = false;
        };
    }, [athletes, raceId, currentUser?.uid, userProfile]);

    if (athletes.length === 0) {
        return null;
    }

    if (isLoading) {
        return (
            <div className="text-center text-gray-400 p-4 flex items-center justify-center gap-2">
                 <svg className="animate-spin h-4 w-4 text-cyan-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing physics...
            </div>
        )
    }

    const athleteNameMap = new Map(athletes.map(a => [a.id, a.name]));

    return (
        <div className="bg-gray-700/50 p-3 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-300 mb-2 border-b border-gray-600 pb-1">Drafting Analysis</h4>
            <table className="w-full text-left table-auto">
                <thead>
                    <tr className="text-xs text-gray-400 uppercase">
                        <th className="font-medium p-1">Athlete</th>
                        <th className="font-medium p-1 text-right">Time Drafting</th>
                        <th className="font-medium p-1 text-right">Energy Saved</th>
                    </tr>
                </thead>
                <tbody className="text-sm">
                    {stats.map(stat => (
                        <tr key={stat.athleteId} className="border-t border-gray-600">
                            <td className="p-1.5 truncate">
                                <div className="flex items-center gap-1.5 truncate">
                                    <span className="truncate">{athleteNameMap.get(stat.athleteId) || 'Unknown'}</span>
                                    {stat.isEstimated && (
                                        <span 
                                            className="inline-block text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-cyan-950/80 text-cyan-300 border border-cyan-500/40 cursor-help"
                                            title="Estimated from GPS proximity and pack speed (no power meter)"
                                        >
                                            Est.
                                        </span>
                                    )}
                                </div>
                            </td>
                            <td className="p-1.5 text-right font-mono">{formatTime(stat.totalTimeDrafting)}</td>
                            <td className="p-1.5 text-right font-mono">{stat.totalEnergySavedKj.toFixed(0)} <span className="text-xs text-gray-400">kJ</span></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default DraftingStats;
